import React, { useMemo } from "react";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import {
  agruparPropuestaPago,
  claveBancoCatalogo,
  configPagoProveedorCompleta,
  configPagoProveedorVacia,
  esMedioTransferenciaProv,
  etiquetaCuentaOrigen,
  formatCuit,
  formatMoneda,
  OPCIONES_MEDIO_PAGO_PROVEEDOR,
  proveedorPuedeTransferencia,
} from "./utils";

const thSx = {
  bgcolor: "#2471A3",
  color: "#fff",
  fontWeight: 600,
  whiteSpace: "nowrap",
  py: 0.75,
  fontSize: 12,
};

const selectSx = {
  fontSize: "0.8rem",
  "& .MuiSelect-select": {
    py: 0.75,
    fontSize: "0.8rem",
    minHeight: "unset !important",
  },
};

const labelSx = {
  fontSize: "0.75rem",
  "&.MuiInputLabel-shrink": { fontSize: "0.75rem" },
};

const ResumenAgrupado = ({ agrupado }) => {
  const { transferencias, cheques, totalTransferencias, totalCheques, etiquetaMedio } =
    agrupado;

  if (!transferencias.length && !cheques.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Completá el medio de pago de cada proveedor para ver el resumen
        agrupado.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
      }}
    >
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "#fafbfc" }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Transferencias
          {transferencias.length > 0 ? (
            <Typography
              component="span"
              variant="body2"
              color="primary.main"
              fontWeight={700}
              sx={{ ml: 1 }}
            >
              {formatMoneda(totalTransferencias)}
            </Typography>
          ) : null}
        </Typography>
        {transferencias.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Ninguna
          </Typography>
        ) : (
          transferencias.map((t) => (
            <Box key={String(t.id_bancos_cuentas)} sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  alignItems: "baseline",
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {t.banco_nombre}
                  {t.nro_cuenta ? (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.75 }}
                    >
                      · {t.nro_cuenta}
                    </Typography>
                  ) : null}
                </Typography>
                <Typography variant="body2" fontWeight={700} color="primary.main">
                  {formatMoneda(t.importe)}
                </Typography>
              </Box>
              {t.proveedores?.length > 1 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {t.proveedores
                    .map((p) => `${p.razon_social} (${formatMoneda(p.importe)})`)
                    .join(" · ")}
                </Typography>
              ) : null}
            </Box>
          ))
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "#fafbfc" }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Cheques
          {cheques.length > 0 ? (
            <Typography
              component="span"
              variant="body2"
              color="primary.main"
              fontWeight={700}
              sx={{ ml: 1 }}
            >
              {formatMoneda(totalCheques)}
            </Typography>
          ) : null}
        </Typography>
        {cheques.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Ninguno
          </Typography>
        ) : (
          cheques.map((c) => (
            <Box
              key={c.cuit}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                mb: 0.75,
                alignItems: "baseline",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {c.razon_social}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {etiquetaMedio(c.medio)} · {formatCuit(c.cuit)}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={700} color="primary.main">
                {formatMoneda(c.importe)}
              </Typography>
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};

const SelectoresEdicion = ({
  config,
  onChange,
  bancosUnicos,
  cuentasBancarias,
  loadingCuentas,
  incompleto,
  disabled = false,
  puedeTransferencia = true,
}) => {
  const medio = config?.medio || "";
  const esTransferencia = esMedioTransferenciaProv(medio);
  const cuentasDelBanco = esTransferencia
    ? (cuentasBancarias || []).filter(
        (c) => claveBancoCatalogo(c) === (config?.codigoBanco || "")
      )
    : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        alignItems: "flex-start",
      }}
    >
      <FormControl
        size="small"
        error={incompleto && !medio}
        disabled={disabled}
        sx={{ minWidth: 140, flex: "1 1 140px" }}
      >
        <InputLabel sx={labelSx}>Medio de pago</InputLabel>
        <Select
          label="Medio de pago"
          value={medio}
          sx={selectSx}
          onChange={(e) => {
            const nextMedio = e.target.value;
            if (
              esMedioTransferenciaProv(nextMedio) &&
              !puedeTransferencia
            ) {
              return;
            }
            onChange({
              ...(config || configPagoProveedorVacia()),
              medio: nextMedio,
              ...(esMedioTransferenciaProv(nextMedio)
                ? {}
                : { codigoBanco: "", id_bancos_cuentas: "" }),
            });
          }}
        >
          <MenuItem value="">Sin definir</MenuItem>
          {OPCIONES_MEDIO_PAGO_PROVEEDOR.map((op) => (
            <MenuItem
              key={op.value}
              value={op.value}
              disabled={
                esMedioTransferenciaProv(op.value) && !puedeTransferencia
              }
            >
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {esTransferencia ? (
        <>
          <FormControl
            size="small"
            error={incompleto && !config?.codigoBanco}
            disabled={disabled || loadingCuentas}
            sx={{ minWidth: 140, flex: "1 1 140px" }}
          >
            <InputLabel sx={labelSx}>Banco</InputLabel>
            <Select
              label="Banco"
              value={config?.codigoBanco || ""}
              sx={selectSx}
              onChange={(e) => {
                const codigoBanco = e.target.value;
                const cuentas = (cuentasBancarias || []).filter(
                  (c) => claveBancoCatalogo(c) === codigoBanco
                );
                onChange({
                  ...(config || configPagoProveedorVacia()),
                  medio,
                  codigoBanco,
                  id_bancos_cuentas:
                    cuentas.length === 1 ? cuentas[0].id_bancos_cuentas : "",
                });
              }}
            >
              {bancosUnicos.map((b) => (
                <MenuItem key={b.codigo} value={b.codigo}>
                  {b.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            error={incompleto && !config?.id_bancos_cuentas}
            disabled={disabled || loadingCuentas || !config?.codigoBanco}
            sx={{ minWidth: 160, flex: "1 1 160px" }}
          >
            <InputLabel sx={labelSx}>Cuenta origen</InputLabel>
            <Select
              label="Cuenta origen"
              value={config?.id_bancos_cuentas || ""}
              sx={selectSx}
              renderValue={(val) => {
                const c = cuentasDelBanco.find(
                  (x) => String(x.id_bancos_cuentas) === String(val)
                );
                if (!c) return val;
                return c.nro_cuenta || etiquetaCuentaOrigen(c);
              }}
              onChange={(e) =>
                onChange({
                  ...(config || configPagoProveedorVacia()),
                  medio,
                  codigoBanco: config?.codigoBanco || "",
                  id_bancos_cuentas: e.target.value,
                })
              }
            >
              {cuentasDelBanco.map((c) => (
                <MenuItem key={c.id_bancos_cuentas} value={c.id_bancos_cuentas}>
                  {c.nro_cuenta
                    ? `${c.nro_cuenta}${c.cbu ? ` — ${c.cbu}` : ""}`
                    : etiquetaCuentaOrigen(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ alignSelf: "center", px: 0.5 }}
        >
          Banco: —
        </Typography>
      )}
    </Box>
  );
};

/**
 * Resumen agrupado + edición de la propuesta de pago por proveedor.
 * La selección del paso Comprobantes es una propuesta editable acá.
 */
const PropuestaPagoMedios = ({
  proveedores = [],
  configPagoPorProveedor = {},
  onConfigPagoChange,
  cuentasBancarias = [],
  loadingCuentas = false,
  editable = true,
  titulo = "Propuesta de pago",
  cuitConError = "",
}) => {
  const bancosUnicos = useMemo(() => {
    const map = new Map();
    (cuentasBancarias || []).forEach((c) => {
      const key = claveBancoCatalogo(c);
      if (!key || map.has(key)) return;
      map.set(key, {
        codigo: key,
        nombre: c.razon_social || c.codigo || key,
      });
    });
    return Array.from(map.values());
  }, [cuentasBancarias]);

  const agrupado = useMemo(
    () =>
      agruparPropuestaPago({
        configPorCuit: configPagoPorProveedor,
        proveedoresConTotal: proveedores,
        cuentasBancarias,
      }),
    [configPagoPorProveedor, proveedores, cuentasBancarias]
  );

  if (!proveedores.length) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <PaymentsOutlinedIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>
          {titulo}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1.5, fontSize: "0.8rem" }}
      >
        {editable
          ? "El medio por proveedor es opcional. Transferencia solo si el proveedor tiene banco y CBU. Cheque y e-cheque no son obligatorios."
          : "Resumen de medios por proveedor según la propuesta confirmada."}
      </Typography>

      <ResumenAgrupado agrupado={agrupado} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 2, mb: 1 }}>
        <AccountBalanceIcon color="action" fontSize="small" />
        <Typography variant="subtitle2" fontWeight={600}>
          Por proveedor
        </Typography>
      </Box>

      {!editable ? null : agrupado.detalleProveedores.some((d) => !d.completa) ? (
        <Alert severity="warning" sx={{ mb: 1.5 }} icon={false}>
          Si elegís transferencia, el proveedor debe tener banco y CBU, y hay
          que indicar cuenta origen.
        </Alert>
      ) : null}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>Proveedor</TableCell>
              <TableCell align="right" sx={thSx}>
                A pagar
              </TableCell>
              <TableCell sx={thSx}>
                {editable ? "Medio / Banco / Cuenta" : "Medio y banco"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agrupado.detalleProveedores.map((fila) => {
              const config =
                configPagoPorProveedor?.[fila.cuit] ||
                configPagoProveedorVacia();
              const incompleto = !configPagoProveedorCompleta(
                config,
                proveedores.find((p) => String(p.cuit) === String(fila.cuit))
              );
              const conError =
                Boolean(cuitConError) && String(fila.cuit) === String(cuitConError);

              return (
                <TableRow
                  key={fila.cuit}
                  sx={{
                    bgcolor: conError
                      ? "rgba(211, 47, 47, 0.08)"
                      : incompleto
                        ? "rgba(237, 108, 2, 0.05)"
                        : undefined,
                    "& td": { verticalAlign: "middle", py: 1.25 },
                    "& td:first-of-type": conError
                      ? { borderLeft: "3px solid", borderLeftColor: "error.main" }
                      : undefined,
                  }}
                >
                  <TableCell sx={{ minWidth: 160 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={conError ? "error.main" : undefined}
                    >
                      {fila.razon_social}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCuit(fila.cuit)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" fontWeight={700} color="primary.main">
                      {formatMoneda(fila.total)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <Box>
                        <SelectoresEdicion
                          config={config}
                          onChange={(next) =>
                            onConfigPagoChange?.(fila.cuit, next)
                          }
                          bancosUnicos={bancosUnicos}
                          cuentasBancarias={cuentasBancarias}
                          loadingCuentas={loadingCuentas}
                          incompleto={incompleto}
                          puedeTransferencia={
                            fila.puedeTransferencia ??
                            proveedorPuedeTransferencia(
                              proveedores.find(
                                (p) => String(p.cuit) === String(fila.cuit)
                              )
                            )
                          }
                        />
                        {!(
                          fila.puedeTransferencia ??
                          proveedorPuedeTransferencia(
                            proveedores.find(
                              (p) => String(p.cuit) === String(fila.cuit)
                            )
                          )
                        ) ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            Sin banco y CBU: no se puede pagar por transferencia.
                          </Typography>
                        ) : null}
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        {agrupado.etiquetaMedio(fila.medio)}
                        {esMedioTransferenciaProv(fila.medio)
                          ? ` · ${
                              (cuentasBancarias || []).find(
                                (c) =>
                                  String(c.id_bancos_cuentas) ===
                                  String(fila.id_bancos_cuentas)
                              )?.razon_social ||
                              fila.codigoBanco ||
                              "—"
                            }`
                          : " · —"}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PropuestaPagoMedios;
